"use client";

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useSignMessage, useDisconnect } from "wagmi";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useWallet as useAptosWallet } from "@aptos-labs/wallet-adapter-react";
import bs58 from "bs58";
import {
  getNonce,
  getSession,
  logout as apiLogout,
  verifySignature,
  type ChainFamily,
} from "./auth-api";

/**
 * Wallet sign-in.
 *
 * The flow is the same for every chain: ask the server for a nonce, sign the exact message
 * it returns, post the signature back: but each ecosystem's signing API differs enough that
 * the differences are worth stating:
 *
 *  • EVM: signs the raw UTF-8 string; signature is hex.
 *  • Solana: signs raw bytes; signature comes back as Uint8Array → base58.
 *  • Aptos: does NOT sign our string. AIP-62 wraps it and signs the wrapper, so we must send
 *    the returned `fullMessage` for the server to verify against (it checks the wrapper
 *    contains our nonce and domain before trusting it).
 */
export const useSession = () => {
  const q = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    // 401 is the normal signed-out state, not an error worth retrying.
    retry: false,
    staleTime: 30_000,
  });
  return {
    session: q.data ?? null,
    isLoading: q.isLoading,
    isSignedIn: !!q.data,
  };
};

export const useSignIn = () => {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { address: evmAddress } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect: disconnectEvm } = useDisconnect();

  const solana = useSolanaWallet();
  const aptos = useAptosWallet();

  const mutation = useMutation({
    mutationFn: async (family: ChainFamily) => {
      setError(null);

      if (family === "evm") {
        if (!evmAddress) throw new Error("Connect an EVM wallet first");
        const { message } = await getNonce("evm", evmAddress);
        const signature = await signMessageAsync({ message });
        return verifySignature({ family: "evm", address: evmAddress, signature });
      }

      if (family === "solana") {
        const address = solana.publicKey?.toBase58();
        if (!address) throw new Error("Connect a Solana wallet first");
        if (!solana.signMessage) throw new Error("This wallet cannot sign messages");
        const { message } = await getNonce("solana", address);
        const sig = await solana.signMessage(new TextEncoder().encode(message));
        return verifySignature({
          family: "solana",
          address,
          signature: bs58.encode(sig),
        });
      }

      // Aptos
      const address = aptos.account?.address?.toString();
      if (!address) throw new Error("Connect an Aptos wallet first");
      const { message, nonce } = await getNonce("aptos", address);
      const res = await aptos.signMessage({ message, nonce });
      const publicKey = aptos.account?.publicKey?.toString();
      if (!publicKey) throw new Error("Wallet did not expose a public key");
      return verifySignature({
        family: "aptos",
        address,
        signature: res.signature.toString(),
        public_key: publicKey,
        // The wrapper the wallet actually signed, see the note above.
        signed_message: res.fullMessage,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (e: Error) => {
      // User rejection is a choice, not a failure worth shouting about.
      const msg = /reject|denied|cancel/i.test(e.message)
        ? "Signature request cancelled"
        : e.message;
      setError(msg);
    },
  });

  const signOut = useCallback(async () => {
    await apiLogout().catch(() => {});
    // Drop the wallet connections too, or the UI shows "connected" with no session.
    // Each is best-effort: a wallet that refuses to disconnect must not block sign-out,
    // since the server session is already gone. Note the adapters differ, wagmi's and
    // Aptos's disconnect are sync (void), Solana's returns a promise.
    try {
      disconnectEvm();
    } catch {
      /* noop */
    }
    try {
      await solana.disconnect();
    } catch {
      /* noop */
    }
    try {
      await Promise.resolve(aptos.disconnect());
    } catch {
      /* noop */
    }
    await qc.invalidateQueries({ queryKey: ["session"] });
  }, [disconnectEvm, solana, aptos, qc]);

  return {
    signIn: mutation.mutateAsync,
    isSigningIn: mutation.isPending,
    error,
    clearError: () => setError(null),
    signOut,
  };
};
